'use client'
import { clsx } from 'clsx'
import * as React from 'react'
import { ButtonLink } from '../button'
import { Logo as BaseHubLogo } from '~/components/mkt/logo'
import { Cross2Icon, SlashIcon } from '@radix-ui/react-icons'
import { UserAvatarDropdown } from '~/components/user-avatar-dropdown'
import { Tab, Tabs, OnAnimationStatusChange } from '~/components/tabs'
import { useIsoLayoutEffect } from '~/hooks/use-iso-layout-effect'
import { RepoPresenceNav } from '~/components/presence/nav'
import { useUserStore } from '~/app/_store/user-store'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { LockSchemaSwitch } from '~/components/lock-schema-switch'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import {
  useIsExternalVisitor,
  useIsGuest,
  useIsGuestInHisBranch,
} from '~/hooks-data/use-user-role'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { FeedbackDialog } from './feedback-dialog'
import { ShareButton } from '~/app/(dash)/[team-slug]/header/share'
import { NotificationsBox } from '~/app/(dash)/[team-slug]/header/notifications'
import { useNotifications } from '~/app/(dash)/[team-slug]/header/notifications/hooks/use-notifications'
import { exitPlayground } from '@basehub/data/replicache/playground/exit-playground'
import { useIsBaseHubEmployee } from '~/hooks/use-is-basehub-employee'
import { IconButton } from '../icon-button'
import { SimpleTooltip } from '../tooltip'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import { isPlaygroundOrPlaygroundCommit } from '@basehub/data/replicache/index'
import { isUserTeam } from '@basehub/data/replicache/mutators/_public-types'
import { startLauncherPortalId } from '~/components/start-launcher/constants'

/* -------------------------------------------------------------------------------------------------
 * Header Root
 * -----------------------------------------------------------------------------------------------*/

export type RootProps = {
  children?: React.ReactNode
  className?: string
}

const Root = ({ children, className }: RootProps) => {
  const headerRef = React.useRef<HTMLHeadElement>(null)

  useIsoLayoutEffect(() => {
    const scrollListener = () => {
      if (!headerRef.current) return
      if (window.scrollY < 15) {
        headerRef.current.style.background = 'transparent'
        headerRef.current.style.borderBottomColor = 'transparent'
        return
      }

      headerRef.current.style.background = 'var(--color-bg)'
      headerRef.current.style.borderBottomColor = 'var(--color-border-solid)'
    }

    scrollListener()
    document.addEventListener('scroll', scrollListener, { passive: true })

    return () => {
      document.removeEventListener('scroll', scrollListener)
    }
  }, [])

  return (
    <header
      ref={headerRef}
      className={clsx(
        'sticky top-0 z-header flex h-header w-full justify-between border-b border-transparent pr-4 transition-colors duration-300',
        className
      )}
    >
      {children}
    </header>
  )
}

const LeftSide = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex items-center text-sm leading-none text-dark-bright grow">
      {children}
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Header Logo
 * -----------------------------------------------------------------------------------------------*/

export type LogoProps = {
  logoHref?: string
  children?: React.ReactNode
  width?: number
  height?: number
} & Omit<React.JSX.IntrinsicElements['a'], 'ref'>

const Logo = ({
  logoHref,
  children,
  width = 16,
  height = 22,
  ...props
}: LogoProps) => {
  const { repoSlug } = useDashboardSlugs()

  return (
    <div>
      <div
        className={clsx(
          repoSlug
            ? 'h-header w-repo-left-nav justify-center bg-dark-shade'
            : 'p-1 pl-7 -m-1',
          'flex items-center space-x-2'
        )}
      >
        <ButtonLink
          unstyled
          href={logoHref ?? '/'}
          {...props}
          // className="-m-1 flex items-center space-x-2 p-1"
        >
          <BaseHubLogo variant="mark" width={width} height={height} />
          {/* <span className="flex h-[16px] items-center whitespace-nowrap rounded-[9999px] bg-dark-control-base/20 px-1 text-[11px] text-dark-control-base">
          v0.0.5
        </span> */}
        </ButtonLink>
        {children}
      </div>
      {repoSlug && <hr className="border-dark-shade -mb-px w-full" />}
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Header BreadCrumbs
 * -----------------------------------------------------------------------------------------------*/

export type BreadCrumbsProps = {
  breadcrumbs: {
    title: string
    isActive: boolean
    href?: string
    menu?: React.ReactNode
    target?: string
    badge?: React.ReactNode
  }[]
}

const BreadCrumbs = ({ breadcrumbs }: BreadCrumbsProps) => {
  const { repoSlug } = useDashboardSlugs()

  return (
    <>
      {breadcrumbs?.map((breadcrumb, index) => {
        const className = clsx(
          'inline-flex max-w-[320px] group whitespace-nowrap py-1 align-middle gap-2 items-center group',
          { 'ml-4': repoSlug && index === 0 },
          !breadcrumb.isActive ? 'text-dark-faint' : 'text-dark-bright'
        )
        return (
          <React.Fragment key={index}>
            {Boolean(!repoSlug || index !== 0) && (
              <BreadCrumbDivider
                spacing={repoSlug || index !== 0 ? 'sm' : 'md'}
              />
            )}
            {breadcrumb.href ? (
              <ButtonLink
                unstyled
                href={breadcrumb.href}
                className={className}
                target={breadcrumb.target}
              >
                <span className="overflow-hidden text-ellipsis w-full group-hover:underline leading-normal">
                  {breadcrumb.title}
                </span>
                {breadcrumb.badge && (
                  <span className={clsx(!breadcrumb.isActive && 'opacity-85')}>
                    {breadcrumb.badge}
                  </span>
                )}
              </ButtonLink>
            ) : (
              <>
                <p className={className}>{breadcrumb.title}</p>
                {breadcrumb.badge}
              </>
            )}
            {breadcrumb.menu}
          </React.Fragment>
        )
      })}
    </>
  )
}

function BreadCrumbDivider({ spacing = 'sm' }: { spacing?: 'sm' | 'md' }) {
  return (
    <SlashIcon
      className={clsx(
        { 'mx-2': spacing === 'sm', 'ml-[13px] mx-3': spacing === 'md' },
        'inline space-x-2 text-dark-faint shrink-0'
      )}
    />
  )
}

/* -------------------------------------------------------------------------------------------------
 * User Nav
 * -----------------------------------------------------------------------------------------------*/

export type UserNavProps = {
  children?: React.ReactNode
  className?: string
}

function UserNav({ children, className }: UserNavProps) {
  const hasRendered = useHasRendered()
  const { isLoading, optimisticUser } = useUserStore((s) => s)
  const { data: repoReplicache } = useRepoReplicache()
  const isGuest = useIsGuest()
  const isExternalVisitor = useIsExternalVisitor()
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { notifications } = useNotifications()
  const { data } = useSelectedTeamAndRepo()
  const team = data?.team
  const branchOrCommit = useSelectedBranchOrCommitQuery().data

  return (
    <nav
      className={clsx(
        'top-0 flex flex-shrink-0 transition-opacity flex-grow select-none items-center justify-end gap-x-3 text-xs text-dark-muted',
        className
      )}
      style={{ opacity: isLoading || !hasRendered ? 0 : 1 }}
    >
      {children}
      {!isGuest && !isExternalVisitor && !branchOrCommit?.playgroundId && (
        <>
          <FeedbackDialog label="Quick help" prefill="" />
        </>
      )}

      <div id={startLauncherPortalId} />

      {repoReplicache?.repoId && (
        <>
          <RepoPresenceNav />
          <LockSchemaSwitch />
        </>
      )}
      {optimisticUser?.id && <RoleBadge />}
      {teamSlug && repoSlug && isUserTeam(team) && <ShareButton />}
      {optimisticUser?.id && <NotificationsBox notifications={notifications} />}
      <UserAvatarDropdown themeSwitcher />
    </nav>
  )
}

function RoleBadge() {
  const data = useSelectedTeamAndRepo().data
  const team = data?.team
  const repo = data?.repo
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const isGuest = useIsGuest()
  const branchOrCommit = useSelectedBranchOrCommitQuery().data
  const isPlayground = branchOrCommit
    ? isPlaygroundOrPlaygroundCommit(branchOrCommit)
    : false
  const isExternalVisitor = useIsExternalVisitor()
  const isBaseHubEmployee = useIsBaseHubEmployee()
  if (!repo || (!isGuest && !isExternalVisitor && !isPlayground)) {
    return null
  }

  let editorTypeBadge: 'View Only' | 'Playground' | 'Contributing' | 'Guest' =
    'View Only'
  if (isPlayground) {
    editorTypeBadge = 'Playground'
  } else if (isGuestInHisBranch) {
    editorTypeBadge = 'Contributing'
  } else if (isGuest) {
    editorTypeBadge = 'Guest'
  }

  return (
    <span
      className={clsx(
        isGuestInHisBranch && !isPlayground
          ? 'text-dark-control-base bg-dark-control-base/15 border-dark-control-base/15'
          : 'text-dark-muted bg-dark-shade border-dark-border-solid',
        'leading-[calc(var(--navbar-element-height)_-_2px)] px-1.5 border rounded-md flex items-center'
      )}
    >
      {editorTypeBadge}
      {editorTypeBadge === 'Playground' && isBaseHubEmployee && (
        <SimpleTooltip content="Exit Playground">
          <IconButton
            className="!p-0.5 size-4 -mr-1 ml-1 hover:!bg-inherit"
            onClick={() => {
              if (!team || !repo) return
              exitPlayground(team.slug, repo.slug)
            }}
          >
            <Cross2Icon />
          </IconButton>
        </SimpleTooltip>
      )}
    </span>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Tabs Nav
 * -----------------------------------------------------------------------------------------------*/

export type TabsNavProps = {
  className?: string
  tabs: Tab[]
  onAnimationStatusChange?: OnAnimationStatusChange
}

function TabsNav({ className, tabs, onAnimationStatusChange }: TabsNavProps) {
  const hasRendered = useHasRendered()

  if (!hasRendered) return <nav />
  return (
    <Tabs
      tabs={tabs}
      className={clsx(
        '!absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center',
        className
      )}
      onAnimationStatusChange={onAnimationStatusChange}
    />
  )
}

/* -------------------------------------------------------------------------------------------------
 * Exports
 * -----------------------------------------------------------------------------------------------*/

export { Root, LeftSide, Logo, BreadCrumbs, UserNav, TabsNav }
